import { isPreconditionReason, PreconditionError } from '@crowdex/agent-contracts';
import {
  parseWorkspaceRequestFrame,
  parseWorkspaceServerFrame,
  type WorkspaceCorrelatedResponseFrame,
  type WorkspacePushFrame,
  type WorkspaceRequestErrorFrame,
  type WorkspaceServerFrame,
  type WorkspaceUncorrelatedResponseFrame,
} from '@crowdex/workspace-protocol';

/**
 * A connection-level failure, as opposed to an error the server answered with. `ambiguous`
 * means the request was sent but no answer arrived, so the operation may have completed.
 */
export class WorkspaceTransportError extends Error {
  constructor(
    message: string,
    readonly ambiguous = false,
  ) {
    super(message);
  }
}

type WorkspaceRpcConnection<Context> = {
  url: string;
  token: string;
  context: Context;
};

type PendingRequest = {
  resolve(frame: WorkspaceCorrelatedResponseFrame): void;
  reject(error: Error): void;
  timeout: ReturnType<typeof setTimeout>;
};

type PendingExchange = {
  resolve(frame: WorkspaceUncorrelatedResponseFrame): void;
  reject(error: Error): void;
  timeout: ReturnType<typeof setTimeout>;
};

export type WorkspaceRpcClientOptions<Context> = {
  connection(): WorkspaceRpcConnection<Context>;
  requestTimeoutMs: number;
  handshakeTimeoutMs?: number;
  totalTimeoutFromConstruction?: boolean;
  connectAttempts?: number;
  retryBaseMs?: number;
  heartbeat?: { intervalMs: number; timeoutMs: number; timeoutMessage: string };
  onPush?(frame: WorkspacePushFrame): void;
  /** Each completed handshake, including lazy reconnects after a dropped socket. */
  onReady?(): void;
  onDisconnect?(error: Error): void;
  messages: {
    closed: string;
    connectionFailed: string;
    handshakeTimeout: string;
    invalidFrame: string;
    requestTimeout: string;
  };
};

/**
 * Shared typed transport for the remote workspace protocol.
 *
 * It owns authentication, handshake, correlation, protocol validation, request
 * timeouts and optional heartbeat/retry behavior. Runtime-specific clients only
 * translate their domain operations into workspace protocol frames.
 */
export class WorkspaceRpcClient<Context> {
  readonly ready: Promise<void>;
  readonly #options: WorkspaceRpcClientOptions<Context>;
  readonly #requests = new Map<string, PendingRequest>();
  readonly #exchanges = new Map<WorkspaceUncorrelatedResponseFrame['type'], PendingExchange>();
  readonly #createdAt = Date.now();
  #socket: WebSocket | null = null;
  #connection: Promise<void> | null = null;
  #connectedContext: Context | null = null;
  #heartbeat: ReturnType<typeof setInterval> | null = null;
  #failConnection: ((error: Error) => void) | null = null;
  #lastPongAt = 0;
  #closed = false;

  constructor(options: WorkspaceRpcClientOptions<Context>) {
    this.#options = options;
    this.ready = this.#connectInitially();
    // Some short-lived clients are torn down before their owner needs to await
    // readiness (for example during a React effect cleanup). Keep the original
    // promise rejecting for explicit consumers, but mark that rejection as
    // observed so an expected close does not become an unhandled runtime error.
    void this.ready.catch(() => undefined);
  }

  get context(): Context | null {
    return this.#connectedContext;
  }

  async request(frame: Record<string, unknown>): Promise<WorkspaceCorrelatedResponseFrame> {
    await this.#connect();
    if (this.#closed) throw new WorkspaceTransportError(this.#options.messages.closed);
    const requestId = crypto.randomUUID();
    const response = new Promise<WorkspaceCorrelatedResponseFrame>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.#requests.delete(requestId);
        reject(new WorkspaceTransportError(this.#options.messages.requestTimeout, true));
      }, this.#requestTimeoutMs());
      this.#requests.set(requestId, { resolve, reject, timeout });
    });
    try {
      this.#sendRequest({ ...frame, request_id: requestId });
    } catch (error) {
      this.#rejectRequest(requestId, toError(error));
    }
    return await response;
  }

  async exchange<Type extends WorkspaceUncorrelatedResponseFrame['type']>(
    frame: Record<string, unknown>,
    expectedType: Type,
  ): Promise<Extract<WorkspaceUncorrelatedResponseFrame, { type: Type }>> {
    await this.#connect();
    if (this.#closed) throw new WorkspaceTransportError(this.#options.messages.closed);
    if (this.#exchanges.has(expectedType)) {
      throw new Error(`A workspace ${expectedType} request is already in progress.`);
    }
    const response = new Promise<WorkspaceUncorrelatedResponseFrame>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.#exchanges.delete(expectedType);
        reject(new WorkspaceTransportError(this.#options.messages.requestTimeout, true));
      }, this.#requestTimeoutMs());
      this.#exchanges.set(expectedType, { resolve, reject, timeout });
    });
    try {
      this.#sendRequest(frame);
    } catch (error) {
      this.#rejectExchange(expectedType, toError(error));
    }
    return (await response) as Extract<WorkspaceUncorrelatedResponseFrame, { type: Type }>;
  }

  send(frame: Record<string, unknown>): void {
    if (this.#closed || this.#socket?.readyState !== WebSocket.OPEN) return;
    this.#sendRequest(frame);
  }

  close(error: Error = new WorkspaceTransportError(this.#options.messages.closed)): void {
    if (this.#closed) return;
    this.#closed = true;
    this.#stopHeartbeat();
    const socket = this.#socket;
    this.#failConnection?.(error);
    this.#rejectAll(error);
    this.#socket = null;
    this.#connection = null;
    socket?.close();
  }

  async #connectInitially(): Promise<void> {
    const attempts = this.#options.connectAttempts ?? 1;
    let lastError: unknown;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      try {
        await this.#connect();
        return;
      } catch (error) {
        lastError = error;
        if (this.#closed || attempt === attempts - 1) break;
        await new Promise((resolve) =>
          setTimeout(resolve, (this.#options.retryBaseMs ?? 250) * 2 ** attempt),
        );
      }
    }
    throw lastError instanceof Error
      ? lastError
      : new WorkspaceTransportError(this.#options.messages.connectionFailed);
  }

  #connect(): Promise<void> {
    if (this.#closed)
      return Promise.reject(new WorkspaceTransportError(this.#options.messages.closed));
    if (this.#connection !== null) return this.#connection;
    const descriptor = this.#options.connection();
    this.#connectedContext = descriptor.context;
    const socket = new WebSocket(descriptor.url);
    this.#socket = socket;
    this.#connection = new Promise<void>((resolve, reject) => {
      let handshakeSettled = false;
      let failed = false;
      const handshakeTimeout = setTimeout(() => {
        fail(new WorkspaceTransportError(this.#options.messages.handshakeTimeout));
        socket.close();
      }, this.#options.handshakeTimeoutMs ?? 10_000);

      const fail = (error: Error): void => {
        if (failed || this.#socket !== socket) return;
        failed = true;
        clearTimeout(handshakeTimeout);
        this.#stopHeartbeat();
        this.#socket = null;
        this.#connection = null;
        this.#failConnection = null;
        if (!handshakeSettled) {
          handshakeSettled = true;
          reject(error);
        }
        this.#rejectAll(error);
        this.#options.onDisconnect?.(error);
      };
      this.#failConnection = fail;

      socket.addEventListener('open', () => {
        socket.send(JSON.stringify({ type: 'hello', token: descriptor.token }));
      });
      socket.addEventListener('message', (event) => {
        let frame: WorkspaceServerFrame;
        try {
          frame = parseWorkspaceServerFrame(event.data);
        } catch (error) {
          // A malformed answer to a known request fails that request, not the connection.
          const requestId = correlatedRequestId(event.data);
          if (requestId !== undefined && this.#requests.has(requestId)) {
            this.#rejectRequest(
              requestId,
              new Error(`${this.#options.messages.invalidFrame} ${toError(error).message}`),
            );
            return;
          }
          fail(new WorkspaceTransportError(this.#options.messages.invalidFrame));
          socket.close();
          return;
        }
        if (frame.type === 'ready') {
          if (!handshakeSettled) {
            handshakeSettled = true;
            clearTimeout(handshakeTimeout);
            this.#startHeartbeat(socket, fail);
            resolve();
            this.#options.onReady?.();
          }
          return;
        }
        if (frame.type === 'pong') {
          this.#lastPongAt = Date.now();
          return;
        }
        if (frame.type === 'error') {
          fail(new WorkspaceTransportError(frame.message));
          socket.close();
          return;
        }
        if (
          frame.type === 'agent_output' ||
          frame.type === 'terminal_output' ||
          frame.type === 'terminal_exit' ||
          frame.type === 'workspace_git_changed'
        ) {
          this.#options.onPush?.(frame);
          return;
        }
        if (frame.type === 'workspace_snapshot' || frame.type === 'workspace_ready') {
          const pending = this.#exchanges.get(frame.type);
          if (pending !== undefined) {
            this.#exchanges.delete(frame.type);
            clearTimeout(pending.timeout);
            pending.resolve(frame);
          }
          return;
        }
        this.#settleRequest(frame);
      });
      socket.addEventListener('error', () => {
        fail(new WorkspaceTransportError(this.#options.messages.connectionFailed));
        socket.close();
      });
      socket.addEventListener('close', () =>
        fail(new WorkspaceTransportError(this.#options.messages.closed)),
      );
    });
    return this.#connection;
  }

  #sendRequest(frame: Record<string, unknown>): void {
    const validated = parseWorkspaceRequestFrame(frame);
    if (validated === null) throw new Error('Unsupported workspace request frame.');
    if (this.#socket?.readyState !== WebSocket.OPEN) {
      throw new WorkspaceTransportError(this.#options.messages.closed);
    }
    try {
      this.#socket.send(JSON.stringify(validated));
    } catch (error) {
      throw new WorkspaceTransportError(toError(error).message);
    }
  }

  #settleRequest(frame: WorkspaceCorrelatedResponseFrame): void {
    const pending = this.#requests.get(frame.request_id);
    if (pending === undefined) return;
    this.#requests.delete(frame.request_id);
    clearTimeout(pending.timeout);
    if (frame.type === 'workspace_request_error') pending.reject(requestFailure(frame));
    else pending.resolve(frame);
  }

  #rejectRequest(requestId: string, error: Error): void {
    const pending = this.#requests.get(requestId);
    if (pending === undefined) return;
    this.#requests.delete(requestId);
    clearTimeout(pending.timeout);
    pending.reject(error);
  }

  #rejectExchange(type: WorkspaceUncorrelatedResponseFrame['type'], error: Error): void {
    const pending = this.#exchanges.get(type);
    if (pending === undefined) return;
    this.#exchanges.delete(type);
    clearTimeout(pending.timeout);
    pending.reject(error);
  }

  #rejectAll(cause: Error): void {
    // These requests were sent; the server may have acted on them before the connection ended.
    const error = new WorkspaceTransportError(cause.message, true);
    for (const pending of this.#requests.values()) {
      clearTimeout(pending.timeout);
      pending.reject(error);
    }
    this.#requests.clear();
    for (const pending of this.#exchanges.values()) {
      clearTimeout(pending.timeout);
      pending.reject(error);
    }
    this.#exchanges.clear();
  }

  #startHeartbeat(socket: WebSocket, fail: (error: Error) => void): void {
    const heartbeat = this.#options.heartbeat;
    if (heartbeat === undefined) return;
    this.#lastPongAt = Date.now();
    this.#heartbeat = setInterval(() => {
      if (Date.now() - this.#lastPongAt > heartbeat.timeoutMs) {
        fail(new WorkspaceTransportError(heartbeat.timeoutMessage));
        socket.close();
      } else if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'ping' }));
      }
    }, heartbeat.intervalMs);
  }

  #stopHeartbeat(): void {
    if (this.#heartbeat !== null) clearInterval(this.#heartbeat);
    this.#heartbeat = null;
  }

  #requestTimeoutMs(): number {
    if (this.#options.totalTimeoutFromConstruction !== true) {
      return this.#options.requestTimeoutMs;
    }
    return Math.max(0, this.#options.requestTimeoutMs - (Date.now() - this.#createdAt));
  }
}

function correlatedRequestId(data: unknown): string | undefined {
  try {
    const value: unknown = JSON.parse(String(data));
    const requestId =
      typeof value === 'object' && value !== null
        ? (value as Record<string, unknown>)['request_id']
        : undefined;
    return typeof requestId === 'string' ? requestId : undefined;
  } catch {
    return undefined;
  }
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

/** A refused request's error: a precondition keeps what is missing, so the page can offer the fix. */
function requestFailure(frame: WorkspaceRequestErrorFrame): Error {
  if (
    (frame.code === 'unconfigured' || frame.code === 'unavailable') &&
    isPreconditionReason(frame.reason)
  )
    return new PreconditionError(frame.code, frame.reason, frame.message);
  return new Error(frame.message);
}
